import { useEffect, useState, useCallback } from "react";

const EVENT_LABELS = {
  page_view: "Page view",
  copy_code: "Code copy",
  register_click: "Register click",
};

function timeAgo(ts) {
  const diff = Math.max(0, Date.now() - new Date(ts).getTime());
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function StatCard({ testId, label, value, hint }) {
  return (
    <div className="flex flex-col rounded-2xl border border-white/10 bg-white/[0.03] px-6 py-7">
      <span className="text-[10px] font-light uppercase tracking-[0.3em] text-white/40">{label}</span>
      <span className="mt-3 text-5xl font-extralight tracking-wide" data-testid={testId} style={{ fontFamily: "Outfit, sans-serif" }}>{value}</span>
      <span className="mt-3 text-[11px] font-light uppercase tracking-[0.2em] text-[#A6B39B]">{hint}</span>
    </div>
  );
}

export default function StatsPage() {
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/analytics/stats`);
      if (!res.ok) throw new Error(String(res.status));
      setStats(await res.json());
      setError(null);
    } catch (e) {
      setError("Could not load stats — check that the backend is running.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const totals = stats?.totals ?? { page_view: 0, copy_code: 0, register_click: 0 };
  const copyRate = totals.page_view ? Math.round((totals.copy_code / totals.page_view) * 100) : 0;
  const registerRate = totals.copy_code ? Math.round((totals.register_click / totals.copy_code) * 100) : 0;
  const maxDaily = Math.max(1, ...(stats?.daily ?? []).map((d) => d.page_view));

  return (
    <div className="min-h-screen bg-[#0E100E] px-6 py-14 text-white" data-testid="stats-page">
      <div className="mx-auto flex w-full max-w-3xl flex-col">
        <a href="/" className="self-start text-[11px] font-light uppercase tracking-[0.28em] text-white/40 transition-colors hover:text-[#A6B39B]">&larr; Back to coupon page</a>
        <div className="mt-8 flex flex-wrap items-end justify-between gap-4">
          <h1 className="text-3xl font-extralight tracking-[0.08em]" style={{ fontFamily: "Outfit, sans-serif" }}>Suruverse analytics</h1>
          <button onClick={load} data-testid="stats-refresh-btn" className="border border-white/25 px-6 py-2.5 text-[11px] font-light uppercase tracking-[0.28em] text-white transition-colors duration-300 hover:border-[#A6B39B] hover:bg-[#73806C]">
            {loading ? "Loading…" : "Refresh"}
          </button>
        </div>

        {error && <p className="mt-8 rounded-xl border border-red-400/30 bg-red-400/10 px-5 py-4 text-sm font-light text-red-200" data-testid="stats-error">{error}</p>}

        {!error && (
          <>
            <div className="mt-10 grid gap-5 sm:grid-cols-3">
              <StatCard testId="stat-page-views" label="Page views" value={totals.page_view} hint="unique sessions" />
              <StatCard testId="stat-code-copies" label="Code copies" value={totals.copy_code} hint={`${copyRate}% of visitors`} />
              <StatCard testId="stat-register-clicks" label="Register clicks" value={totals.register_click} hint={`${registerRate}% of copiers`} />
            </div>

            <h2 className="mt-14 text-[11px] font-light uppercase tracking-[0.3em] text-[#A6B39B]">Daily breakdown</h2>
            <div className="mt-5 flex flex-col gap-3" data-testid="stats-daily-list">
              {(!stats?.daily || stats.daily.length === 0) && (
                <p className="text-sm font-light text-white/50">No events yet — visit the coupon page to generate some.</p>
              )}
              {(stats?.daily ?? []).map((d) => (
                <div key={d.date} className="flex items-center gap-4">
                  <span className="w-24 flex-shrink-0 text-xs font-light tracking-wider text-white/50">{d.date}</span>
                  <div className="h-2 flex-1 overflow-hidden rounded-full bg-white/10">
                    <div className="h-full rounded-full bg-[#73806C]" style={{ width: `${Math.round((d.page_view / maxDaily) * 100)}%` }} />
                  </div>
                  <span className="w-44 flex-shrink-0 text-right text-[11px] font-light uppercase tracking-wider text-white/40">{d.page_view} views · {d.copy_code} copies · {d.register_click} clicks</span>
                </div>
              ))}
            </div>

            <h2 className="mt-14 text-[11px] font-light uppercase tracking-[0.3em] text-[#A6B39B]">Recent events</h2>
            <div className="mt-5 flex flex-col divide-y divide-white/5" data-testid="stats-recent-list">
              {(!stats?.recent || stats.recent.length === 0) && (
                <p className="text-sm font-light text-white/50">Nothing recorded yet.</p>
              )}
              {(stats?.recent ?? []).map((r) => (
                <div key={r.id} className="flex items-center justify-between gap-4 py-3 text-sm font-light">
                  <span className="rounded-full border border-[#A6B39B]/40 px-3 py-1 text-[10px] uppercase tracking-[0.2em] text-[#A6B39B]">{EVENT_LABELS[r.event_type] ?? r.event_type}</span>
                  <span className="flex-1 truncate text-left text-white/40">{r.referrer ? new URL(r.referrer).hostname : "direct"}</span>
                  <span className="flex-shrink-0 text-xs text-white/35">{timeAgo(r.ts)}</span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
